/**
 * First-run design tokens — SIGNUP-2..5.
 *
 * Ported from the "Orbit First Run v4" prototype (Sept 2026). One
 * place for the palettes each step washes the backdrop with, the
 * craft accents, the goal → world table and the small line-icon set
 * the chips and capsules use. Copy that names a real Orbit thing
 * (plans, prices, features) comes from the app's own config, never
 * from here.
 */

export type CraftKey = 'photo' | 'film' | 'both' | 'other';

export const CRAFT: Record<CraftKey, { label: string; acc: string; desc: string }> = {
  photo: { label: 'Photographer', acc: '#E8B54A', desc: 'Weddings, portraits, brands, editorial.' },
  film: { label: 'Filmmaker', acc: '#7FB2E8', desc: 'Brand films, weddings, documentaries.' },
  both: { label: 'Both', acc: '#7ECFCF', desc: 'Stills and motion, sometimes on the same day.' },
  other: { label: 'Something else', acc: '#B5D334', desc: 'You can tell us more later.' },
};

/** Backdrop wash palettes: [base, a, b, c]. */
export const PAL: Record<string, [string, string, string, string]> = {
  auth: ['#0E0B2A', '#3B2AB8', '#2F6BE8', '#E86A9A'],
  s1: ['#120A34', '#4A2AD0', '#E05AB8', '#FF9A5E'],
  s2: ['#061C2C', '#1266A8', '#2FD0C0', '#8A6BFF'],
  s4: ['#1A0A2A', '#6A2AB0', '#F06AA0', '#FFC24A'],
  s5: ['#071630', '#1A4AA8', '#4FA0FF', '#B86BFF'],
  s6: ['#200820', '#8A1F7A', '#FF5A9A', '#FFB07A'],
  s8: ['#04201E', '#0A6A6A', '#3AF0D0', '#7A5AFF'],
  s9: ['#080E30', '#1A34A0', '#5A9AFF', '#E05AC8'],
  s10: ['#0E1A06', '#3A6A12', '#B8E83A', '#2FD0C0'],
  s11: ['#0C0C24', '#3A3AA0', '#9A8AFF', '#FF7AC8'],
  s12: ['#180A2E', '#6A1FB0', '#FFC24A', '#3AF0D0'],
  s13: ['#0E0B2A', '#3B2AB8', '#B85AFF', '#FF8A6E'],
  s14: ['#2A1004', '#A04A12', '#FFC85A', '#FF6A8A'],
  t1: ['#081A1C', '#0F4A52', '#7ECFCF', '#6A5AD0'],
  t2: ['#1E1408', '#6A4A12', '#E8B54A', '#C4607A'],
  t3: ['#10160A', '#3A5A12', '#B5D334', '#3FA3A0'],
  t4: ['#0A1226', '#1E3A8C', '#7FB2E8', '#6A4FC4'],
  t5: ['#1E0E0C', '#6A2A22', '#F29A7E', '#B45BC8'],
  space: ['#0A0E1A', '#1A2460', '#4A3AA8', '#BC942F'],
  today: ['#160E22', '#3A1F5A', '#7A3559', '#C4625B'],
  photo: ['#1E1408', '#7A4A1A', '#E8B54A', '#C4607A'],
  film: ['#0A1226', '#1E3A8C', '#7FB2E8', '#6A4FC4'],
  both: ['#081A1C', '#135A5A', '#7ECFCF', '#4A6AC4'],
  other: ['#10160A', '#3A5A12', '#B5D334', '#2E8A7A'],
};

export const TOUR_ACC = ['#7ECFCF', '#E8B54A', '#B5D334', '#7FB2E8', '#F29A7E'];
export const MISSION_BLUE = '#2F6BE8';
export const PASSPORT_GOLD = '#BC942F';

export type GoalKey = 'bookings' | 'revenue' | 'galleries';
export type PlanetSkin = 'ember' | 'glacier' | 'brass' | 'rose' | 'tide' | 'slate';

export const GOALS: Record<
  GoalKey,
  {
    label: string;
    hint: string;
    world: string;
    skin: PlanetSkin;
    ringed?: boolean;
    ink: string;
    prefix: string;
    suffix: string;
    unit: string;
    fill: string;
    sug: string;
    def: string;
  }
> = {
  bookings: {
    label: 'Bookings', hint: '12 booked projects', world: 'Dunes', skin: 'ember', ink: '#D4541F',
    prefix: '', suffix: 'booked', unit: 'booking', fill: 'Mark a project booked and this moves.',
    sug: 'A full wedding season', def: '12',
  },
  revenue: {
    label: 'Revenue', hint: '$50,000 paid', world: 'Halo', skin: 'brass', ringed: true, ink: '#B8860B',
    prefix: '$', suffix: '', unit: 'paid invoice', fill: 'Get an invoice paid and this moves.',
    sug: 'The first $50k year', def: '50,000',
  },
  galleries: {
    label: 'Galleries', hint: '24 galleries delivered', world: 'Fathom', skin: 'tide', ink: '#2D6E70',
    prefix: '', suffix: 'delivered', unit: 'gallery', fill: 'Deliver a gallery and this moves.',
    sug: 'Twenty-four galleries out the door', def: '24',
  },
};

/** 24×24 stroke paths. */
export const IC: Record<string, string> = {
  mail: 'M3 6h18v12H3z M3 7l9 6 9-6',
  chat: 'M4 5h16v11H9l-5 4z',
  phone: 'M7 3h4l2 5-2.5 1.5a9 9 0 0 0 4 4L16 11l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 5 5a2 2 0 0 1 2-2z',
  camera: 'M4 8h4l2-3h4l2 3h4v11H4z M12 17a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  film: 'M4 5h16v14H4z M4 9h16 M4 15h16 M8 5v14 M16 5v14',
  both: 'M3 9h7v10H3z M14 5h7v10h-7z M10 14l4-4',
  spark: 'M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z',
  upload: 'M12 16V5 M7 10l5-5 5 5 M4 19h16',
  cloud: 'M7 18a4 4 0 0 1 .5-8 5.5 5.5 0 0 1 10.5 1.5A3.5 3.5 0 0 1 17.5 18z',
  doc: 'M6 3h9l4 4v14H6z M15 3v4h4 M9 12h6 M9 16h6',
  coin: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M9.5 14.5c.5 1 1.5 1.5 2.5 1.5 1.7 0 2.5-.8 2.5-1.8 0-2.4-5-1-5-3.4 0-1 .8-1.8 2.5-1.8 1 0 2 .5 2.5 1.5 M12 7v1.5 M12 15.5V17',
  calendar: 'M4 6h16v14H4z M4 10h16 M8 3v4 M16 3v4',
  globe: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M3 12h18 M12 3c3 3 3 15 0 18 M12 3c-3 3-3 15 0 18',
  tag: 'M3 12V4h8l10 10-8 8z M7.5 8.5h.01',
  check: 'M5 12l4.5 4.5L19 7',
  image: 'M4 5h16v14H4z M4 15l5-4 4 3 3-2 4 3 M16 9h.01',
  pin: 'M12 21s-6-5.5-6-11a6 6 0 0 1 12 0c0 5.5-6 11-6 11z M12 12a2 2 0 1 0 0-4 2 2 0 0 0 0 4z',
  users: 'M9 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6 M16 4a4 4 0 0 1 0 8 M21 20c0-3-1.8-5.2-4.5-5.8',
  bolt: 'M13 2L4 14h7l-1 8 9-12h-7z',
  star: 'M12 3l2.7 5.6 6.3.9-4.5 4.3 1.1 6.2L12 17l-5.6 3 1.1-6.2L3 9.5l6.3-.9z',
  flag: 'M5 21V4 M5 4h12l-2 4 2 4H5',
  planet: 'M12 19a7 7 0 1 0 0-14 7 7 0 0 0 0 14z M2.5 9.5c4-3 15-4 19 3 M21.5 14.5c-4 3-15 4-19-3',
  ring: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 17a5 5 0 1 0 0-10',
  sidebar: 'M4 5h16v14H4z M9 5v14',
  home: 'M4 11l8-7 8 7v9H4z',
  lock: 'M6 11h12v10H6z M9 11V7a3 3 0 0 1 6 0v4',
  pen: 'M4 20l4-1 11-11-3-3L5 16z M14 6l3 3',
  wand: 'M4 20L15 9 M13 4l1 2 2 1-2 1-1 2-1-2-2-1 2-1z M19 12l.5 1.2 1.2.5-1.2.5-.5 1.3-.5-1.3-1.2-.5 1.2-.5z',
  eye: 'M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
  receipt: 'M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6 M9 12h6',
  send: 'M22 2L11 13 M22 2l-7 20-4-9-9-4z',
  plus: 'M12 5v14 M5 12h14',
};

const ICON_RULES: Array<[RegExp, string]> = [
  [/apple/i, 'lock'], [/google/i, 'globe'], [/email|mail/i, 'mail'], [/^text$|message|note/i, 'chat'], [/call|phone/i, 'phone'],
  [/photograph|photo|golden|portrait|wedding$/i, 'camera'], [/film|delivery|documentary/i, 'film'], [/^both$/i, 'both'], [/something else/i, 'spark'],
  [/csv|upload/i, 'upload'], [/pixieset|import/i, 'cloud'], [/inquiry|form|brief|session|shoot$/i, 'doc'], [/paid|\$|€|revenue|invoice|money|deposit/i, 'coin'],
  [/booking|booked|date|calendar|day/i, 'calendar'], [/site|front door/i, 'globe'], [/tag/i, 'tag'], [/gallery|galleries|resolution|favourite|download/i, 'image'],
  [/city|porto|sintra|lisbon/i, 'pin'], [/client|people|teammate/i, 'users'], [/draft|answer|question|ask|nova/i, 'wand'], [/ring|compact/i, 'ring'],
  [/sidebar/i, 'sidebar'], [/contract|signed|timeline/i, 'pen'], [/adobe|expense|cost|cc/i, 'receipt'], [/review|star|first/i, 'star'], [/ready|open/i, 'check'],
  [/goal|shoots|world/i, 'planet'], [/plan|month|year/i, 'calendar'], [/thank/i, 'send'], [/logo/i, 'plus'], [/account/i, 'eye'],
];

export function iconFor(label: string): string {
  for (const [re, k] of ICON_RULES) if (re.test(label)) return IC[k];
  return '';
}

/** Keyframes the kit relies on. Injected once by FirstRunBackdrop. */
export const FIRSTRUN_KEYFRAMES = `
@keyframes frOrbBreath{0%,100%{transform:scale(1) rotate(0deg)}50%{transform:scale(1.06) rotate(10deg)}}
@keyframes frOrbPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}
@keyframes frRingOut{0%{transform:scale(.6);opacity:.85}100%{transform:scale(1.3);opacity:0}}
@keyframes frFloatA{0%{transform:translate(0,0) rotate(0deg)}25%{transform:translate(22px,-18px) rotate(-2.5deg)}50%{transform:translate(6px,-34px) rotate(1deg)}75%{transform:translate(-20px,-12px) rotate(2.4deg)}100%{transform:translate(0,0) rotate(0deg)}}
@keyframes frFloatB{0%{transform:translate(0,0) rotate(0deg)}30%{transform:translate(-26px,14px) rotate(2deg)}55%{transform:translate(-10px,-22px) rotate(-1.6deg)}80%{transform:translate(18px,10px) rotate(-2.2deg)}100%{transform:translate(0,0) rotate(0deg)}}
@keyframes frFloatC{0%{transform:translate(0,0) rotate(0deg)}35%{transform:translate(16px,24px) rotate(1.8deg)}65%{transform:translate(-22px,8px) rotate(-2.6deg)}100%{transform:translate(0,0) rotate(0deg)}}
@keyframes frBlueBreath{0%,100%{box-shadow:0 12px 34px rgba(47,107,232,.45)}50%{box-shadow:0 14px 60px rgba(47,107,232,.85)}}
@keyframes frHaloPulse{0%,100%{filter:drop-shadow(0 0 0 rgba(188,148,47,0))}50%{filter:drop-shadow(0 0 22px rgba(188,148,47,.6))}}
@keyframes frStampIn{0%{transform:scale(2.4) rotate(-14deg);opacity:0}55%{transform:scale(.94) rotate(-9deg);opacity:1}100%{transform:scale(1) rotate(-9deg);opacity:1}}
@keyframes frSparkle{0%,100%{opacity:.2;transform:translateY(0) scale(.8) rotate(0deg)}50%{opacity:1;transform:translateY(-14px) scale(1.15) rotate(45deg)}}
@keyframes frRingSpin{to{transform:rotate(360deg)}}
@keyframes frBob{0%,100%{transform:translateY(0)}50%{transform:translateY(-7px)}}
@keyframes frNovaBob{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-3px) rotate(-4deg)}}
@keyframes frGlowPulse{0%,100%{box-shadow:0 0 0 1px rgba(232,220,255,.38),0 0 46px rgba(214,150,255,.38)}50%{box-shadow:0 0 0 1px rgba(240,230,255,.6),0 0 84px rgba(220,160,255,.68)}}
@keyframes frShimmer{0%{background-position:160% 0}55%,100%{background-position:-60% 0}}
@keyframes frTwinkle{0%,100%{opacity:.3}50%{opacity:.95}}
@keyframes frDrift1{0%,100%{transform:translate(0,0)}50%{transform:translate(9%,7%)}}
@keyframes frDrift2{0%,100%{transform:translate(0,0)}50%{transform:translate(-8%,10%)}}
@keyframes frDrift3{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(6%,-6%) scale(1.1)}}
@keyframes frRise{from{opacity:0;transform:translateY(14px) scale(.96)}to{opacity:1;transform:none}}
.fr-cta{position:sticky;bottom:0;margin:0 -16px;padding:28px 16px max(22px,env(safe-area-inset-bottom));background:linear-gradient(180deg,rgba(11,7,9,0) 0%,rgba(11,7,9,.92) 34%,#0B0709 100%)}
@media (min-width:768px){.fr-cta{position:relative;margin:0 auto;padding:18px 0 clamp(24px,3vw,44px);background:transparent}}
@media (prefers-reduced-motion: reduce){.fr-motion *{animation:none!important;transition:none!important}}
`;
